<template>
  <div class="recipient-info-step">
    <div class="recipient-info-content">
      <!-- Title -->
      <div class="title-section">
        <h2 class="step-title">{{ step.title }}</h2>
        <p v-if="step.subtitle" class="step-subtitle">{{ step.subtitle }}</p>
      </div>

      <!-- Form Fields -->
      <div class="form-fields">
        <!-- Name Input -->
        <div class="field-group">
          <label class="field-label">Имя</label>
          <InputText
            v-model="recipientName"
            placeholder="Введите имя"
            class="field-input"
            @update:model-value="updateName"
          />
        </div>

        <!-- Relationship Select -->
        <div class="field-group">
          <label class="field-label">Кем близкий вам приходится</label>
          <Select
            v-model="selectedRelationship"
            :options="step.relationshipOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Выберите"
            class="field-select"
            @update:model-value="updateRelationship"
          />
        </div>

        <!-- Gender Select -->
        <div class="field-group">
          <label class="field-label">Пол</label>
          <Select
            v-model="selectedGender"
            :options="genderOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Выберите"
            class="field-select"
            @update:model-value="updateGender"
          />
        </div>

        <!-- Age Select -->
        <div class="field-group">
          <label class="field-label">Возраст</label>
          <Select
            v-model="selectedAge"
            :options="step.ageOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Выберите"
            class="field-select"
            @update:model-value="updateAge"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import InputText from 'primevue/inputtext'
import Select from 'primevue/select'
import { useGiftModal } from '@/features/gift/store'
import type { GiftStep, GenderValue } from '@/features/gift/types/gift-config'

interface Props {
  step: GiftStep & { type: 'recipient_info' }
}

const props = defineProps<Props>()
const giftStore = useGiftModal()

const recipientName = ref<string>('')
const selectedRelationship = ref<string | null>(null)
const selectedGender = ref<GenderValue | null>(null)
const selectedAge = ref<string | null>(null)

const genderOptions = [
  { value: 'male', label: 'Мужской' },
  { value: 'female', label: 'Женский' }
]

function updateName() {
  giftStore.updateGiftData(`${props.step.id}_name`, recipientName.value)
}

function updateRelationship() {
  giftStore.updateGiftData(`${props.step.id}_relationship`, selectedRelationship.value)
}

function updateGender() {
  giftStore.updateGiftData(`${props.step.id}_gender`, selectedGender.value)
}

function updateAge() {
  giftStore.updateGiftData(`${props.step.id}_age`, selectedAge.value)
}

onMounted(() => {
  const data = giftStore.giftModal.giftData
  recipientName.value = data[`${props.step.id}_name`] || ''
  selectedRelationship.value = data[`${props.step.id}_relationship`] || null
  selectedGender.value = data[`${props.step.id}_gender`] || null
  selectedAge.value = data[`${props.step.id}_age`] || null
})
</script>

<style scoped>
.recipient-info-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 40px 20px;
}

.recipient-info-content {
  max-width: 500px;
  width: 100%;
}

.title-section {
  margin-bottom: 32px;
  text-align: left;
}

.step-title {
  font-family: 'Gilroy';
  font-size: 32px;
  line-height: 1.09;
  font-weight: 700;
  letter-spacing: -0.64px;
  color: var(--primary-blue-100);
  margin: 0 0 12px 0;
}

.step-subtitle {
  font-family: 'Inter';
  font-size: 15px;
  line-height: 1.3;
  letter-spacing: -0.64px;
  color: var(--primary-blue-80);
  margin: 0;
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  font-size: 14px;
  font-weight: 500;
  color: #1d1d1f;
}

.field-input,
.field-select {
  width: 100%;
  height: 40px !important;
}

@media (min-width: 1024px) {
  .title-section {
    text-align: left;
    gap: 8px;
  }

  .step-title {
    font-size: 48px;
    line-height: 1.2;
    letter-spacing: -0.04em;
  }

  .step-subtitle {
    font-size: 20px;
    line-height: 1.3;
    letter-spacing: -0.04em;
  }
}
</style>

